import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertCircle, FiCheckCircle, FiPaperclip, FiRefreshCw, FiSend, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import useCallCenterT from "../../hooks/useCallCenterT";
import ListingEmptyState from "./listing/ListingEmptyState";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
} from "./listing/listingStyles";
import { formatPhoneDisplay } from "../../utils/phoneNumberUtils";
import {
  checkTicketWhatsAppPhone,
  downloadTicketWhatsAppMedia,
  getTicketWhatsAppThread,
  sendTicketWhatsAppMedia,
  sendTicketWhatsAppMessage,
} from "../../service/CallCenterTickets";

const POLL_INTERVAL_MS = 5000;
const MAX_FILE_BYTES = 16 * 1024 * 1024;
const ACCEPTED_FILE_TYPES =
  "image/jpeg,image/png,image/webp,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,audio/mpeg,video/mp4";

const formatThreadDateTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusLabelKey = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "SENT":
      return "ccWhatsAppStatusSent";
    case "DELIVERED":
      return "ccWhatsAppStatusDelivered";
    case "READ":
      return "ccWhatsAppStatusRead";
    case "FAILED":
      return "ccWhatsAppStatusFailed";
    case "PENDING":
      return "ccWhatsAppStatusPending";
    default:
      return "";
  }
};

const statusTone = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "READ":
      return "text-emerald-600";
    case "DELIVERED":
      return "text-blue-600";
    case "FAILED":
      return "text-red-600";
    case "PENDING":
      return "text-amber-600";
    default:
      return "text-slate-500";
  }
};

const ThreadSkeleton = () => (
  <div className="space-y-3 p-1" aria-hidden="true">
    {Array.from({ length: 4 }).map((_, index) => (
      <div
        key={index}
        className={`flex ${index % 2 === 0 ? "justify-start" : "justify-end"}`}
      >
        <div className="h-14 w-[68%] animate-pulse rounded-2xl bg-slate-100" />
      </div>
    ))}
  </div>
);

const PhoneCheckBadge = ({ phoneCheck, t }) => {
  if (phoneCheck.status === "checking") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
        {t("ccWhatsAppCheckingNumber")}
      </span>
    );
  }
  if (phoneCheck.status === "error") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
        <FiAlertCircle className="h-3.5 w-3.5" />
        {t("ccWhatsAppCheckFailed")}
      </span>
    );
  }
  if (phoneCheck.status === "done" && phoneCheck.onWhatsApp) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
        <FiCheckCircle className="h-3.5 w-3.5" />
        {t("ccWhatsAppNumberValid")}
      </span>
    );
  }
  if (phoneCheck.status === "done" && !phoneCheck.onWhatsApp) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
        <FiAlertCircle className="h-3.5 w-3.5" />
        {t("ccWhatsAppNumberInvalidHint")}
      </span>
    );
  }
  return null;
};

const TicketWhatsAppThread = ({ ticketId, active = true, refreshKey = 0 }) => {
  const t = useCallCenterT();
  const [thread, setThread] = useState({ phoneNumber: "", availablePhoneNumbers: [], messages: [] });
  const [selectedPhone, setSelectedPhone] = useState("");
  const [initialLoading, setInitialLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [draftText, setDraftText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [downloadingMessageId, setDownloadingMessageId] = useState(null);
  const [phoneCheck, setPhoneCheck] = useState({ status: "idle", onWhatsApp: null });
  const fileInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const selectedPhoneRef = useRef("");

  useEffect(() => {
    selectedPhoneRef.current = selectedPhone;
  }, [selectedPhone]);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
  }, []);

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 80;
  };

  const loadThread = useCallback(
    async (phoneNumber, { silent = false } = {}) => {
      if (!ticketId) {
        setThread({ phoneNumber: "", availablePhoneNumbers: [], messages: [] });
        setSelectedPhone("");
        return;
      }

      const targetPhone = phoneNumber || selectedPhoneRef.current || undefined;
      if (silent) {
        setRefreshing(true);
      } else {
        setInitialLoading(true);
      }
      setLoadError("");

      try {
        const response = await getTicketWhatsAppThread(ticketId, targetPhone);
        const availablePhoneNumbers = Array.isArray(response?.availablePhoneNumbers)
          ? response.availablePhoneNumbers
          : [];
        const resolvedPhone =
          response?.phoneNumber || targetPhone || availablePhoneNumbers[0] || "";
        const messages = Array.isArray(response?.messages) ? response.messages : [];

        setThread({
          phoneNumber: resolvedPhone,
          availablePhoneNumbers,
          messages,
        });

        if (resolvedPhone && resolvedPhone !== selectedPhoneRef.current) {
          setSelectedPhone(resolvedPhone);
        }

        if (stickToBottomRef.current) {
          window.requestAnimationFrame(() => scrollToBottom(silent ? "auto" : "smooth"));
        }
      } catch (error) {
        const message = error?.message || t("ccWhatsAppThreadLoadFailed");
        setLoadError(message);
        if (!silent) {
          toast.error(message);
        }
      } finally {
        setInitialLoading(false);
        setRefreshing(false);
      }
    },
    [ticketId, t, scrollToBottom]
  );

  useEffect(() => {
    if (!ticketId) return;
    stickToBottomRef.current = true;
    selectedPhoneRef.current = "";
    setSelectedPhone("");
    loadThread(undefined, { silent: false });
  }, [ticketId, refreshKey, loadThread]);

  useEffect(() => {
    if (!active || !ticketId || !selectedPhone) return undefined;
    const intervalId = window.setInterval(() => {
      loadThread(selectedPhone, { silent: true });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [active, ticketId, selectedPhone, loadThread]);

  useEffect(() => {
    if (!ticketId || !selectedPhone) {
      setPhoneCheck({ status: "idle", onWhatsApp: null });
      return undefined;
    }
    let cancelled = false;
    const verifyPhone = async () => {
      try {
        setPhoneCheck({ status: "checking", onWhatsApp: null });
        const result = await checkTicketWhatsAppPhone(ticketId, selectedPhone);
        if (!cancelled) {
          setPhoneCheck({ status: "done", onWhatsApp: Boolean(result?.onWhatsApp) });
        }
      } catch {
        if (!cancelled) {
          setPhoneCheck({ status: "error", onWhatsApp: null });
        }
      }
    };
    verifyPhone();
    return () => {
      cancelled = true;
    };
  }, [ticketId, selectedPhone]);

  const handlePhoneChange = (event) => {
    const nextPhone = event.target.value;
    stickToBottomRef.current = true;
    setSelectedPhone(nextPhone);
    loadThread(nextPhone, { silent: false });
  };

  const validateFile = (file) => {
    if (!file) return null;
    if (file.size > MAX_FILE_BYTES) {
      return t("ccWhatsAppFileTooLarge");
    }
    return null;
  };

  const handleFileSelected = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setSelectedFile(null);
      return;
    }
    const error = validateFile(file);
    if (error) {
      toast.error(error);
      event.target.value = "";
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSend = async () => {
    const text = String(draftText || "").trim();
    if ((!text && !selectedFile) || !ticketId || !selectedPhone) return;

    if (selectedFile) {
      const fileError = validateFile(selectedFile);
      if (fileError) {
        toast.error(fileError);
        return;
      }
    }

    try {
      setSending(true);
      stickToBottomRef.current = true;
      if (selectedFile) {
        await sendTicketWhatsAppMedia(ticketId, {
          phoneNumber: selectedPhone,
          file: selectedFile,
          caption: text || undefined,
        });
      } else {
        await sendTicketWhatsAppMessage(ticketId, { phoneNumber: selectedPhone, text });
      }
      setDraftText("");
      clearSelectedFile();
      toast.success(t("ccWhatsAppMessageSent"));
      await loadThread(selectedPhone, { silent: true });
    } catch (error) {
      toast.error(error?.message || t("ccWhatsAppSendFailed"));
    } finally {
      setSending(false);
    }
  };

  const handleComposerKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!sending) {
        handleSend();
      }
    }
  };

  const handleDownload = async (message) => {
    if (!message?.downloadable || !ticketId) return;
    try {
      setDownloadingMessageId(message.id);
      await downloadTicketWhatsAppMedia(
        ticketId,
        message.id,
        message.mediaFileName || "attachment"
      );
    } catch (error) {
      toast.error(error?.message || t("ccWhatsAppDownloadFailed"));
    } finally {
      setDownloadingMessageId(null);
    }
  };

  if (initialLoading && !thread.messages.length && !thread.availablePhoneNumbers.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <ThreadSkeleton />
      </div>
    );
  }

  if (!thread.availablePhoneNumbers.length && !selectedPhone) {
    return (
      <ListingEmptyState
        icon={FiAlertCircle}
        title={t("ccWhatsAppNoPhone")}
        hint={t("ccWhatsAppNoPhoneHint")}
      />
    );
  }

  const phoneOptions = thread.availablePhoneNumbers.length
    ? thread.availablePhoneNumbers
    : selectedPhone
      ? [selectedPhone]
      : [];

  return (
    <div className="flex min-h-[420px] flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <label className={FILTER_LABEL_CLASS} htmlFor="whatsapp-phone-select">
              {t("ccWhatsAppSelectNumber")}
            </label>
            <select
              id="whatsapp-phone-select"
              value={selectedPhone}
              onChange={handlePhoneChange}
              className={FILTER_FIELD_CLASS}
            >
              {phoneOptions.map((phone) => (
                <option key={phone} value={phone}>
                  {formatPhoneDisplay(phone) || phone}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <PhoneCheckBadge phoneCheck={phoneCheck} t={t} />
            <button
              type="button"
              onClick={() => loadThread(selectedPhone, { silent: false })}
              disabled={refreshing || !selectedPhone}
              title={t("ccRefresh")}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
            >
              <FiRefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      {loadError ? (
        <div className="mx-4 mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{loadError}</span>
          <button
            type="button"
            onClick={() => loadThread(selectedPhone, { silent: false })}
            className="font-semibold hover:underline"
          >
            {t("ccRefresh")}
          </button>
        </div>
      ) : null}

      <div
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        className="min-h-[280px] flex-1 overflow-y-auto bg-[#ece5dd]/40 px-4 py-4"
        aria-live="polite"
        aria-relevant="additions"
      >
        {!thread.messages.length ? (
          <ListingEmptyState
            icon={FiSend}
            title={t("ccWhatsAppEmptyTitle")}
            hint={t("ccWhatsAppEmpty")}
          />
        ) : (
          <div className="space-y-3">
            {thread.messages.map((message) => {
              const isOutbound = message.direction === "OUTBOUND";
              const statusKey = statusLabelKey(message.status);
              const failed = String(message.status || "").toUpperCase() === "FAILED";
              return (
                <div
                  key={message.id}
                  className={`flex ${isOutbound ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                      isOutbound
                        ? failed
                          ? "bg-red-500 text-white"
                          : "bg-[#25D366] text-white"
                        : "border border-slate-200 bg-white text-slate-800"
                    }`}
                  >
                    {message.body ? (
                      <div className="whitespace-pre-wrap break-words">{message.body}</div>
                    ) : null}
                    {message.hasMedia ? (
                      <div className={`mt-2 ${isOutbound ? "text-white" : "text-slate-800"}`}>
                        <div className="text-xs font-semibold">
                          {message.mediaFileName || t("ccWhatsAppAttachment")}
                        </div>
                        {message.downloadable ? (
                          <button
                            type="button"
                            onClick={() => handleDownload(message)}
                            disabled={downloadingMessageId === message.id}
                            className={`mt-1 text-xs underline disabled:opacity-50 ${
                              isOutbound ? "text-white" : "text-[#128C7E]"
                            }`}
                          >
                            {downloadingMessageId === message.id
                              ? t("ccProcessing")
                              : t("ccWhatsAppDownload")}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                    {!message.body && !message.hasMedia ? <div>{t("ccEmDash")}</div> : null}
                    <div
                      className={`mt-1.5 flex flex-wrap items-center gap-x-1 text-[10px] ${
                        isOutbound ? "text-white/85" : "text-slate-500"
                      }`}
                    >
                      {isOutbound && message.agentName ? (
                        <span className="font-medium">{message.agentName}</span>
                      ) : null}
                      <span>{formatThreadDateTime(message.createdAt)}</span>
                      {statusKey ? (
                        <span className={isOutbound ? "text-white/90" : statusTone(message.status)}>
                          · {t(statusKey)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3">
        {selectedFile ? (
          <div className="mb-3 flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
            <div className="flex min-w-0 items-center gap-2">
              <FiPaperclip className="h-4 w-4 shrink-0 text-slate-400" />
              <span className="truncate">
                {t("ccWhatsAppSelectedFile")}: {selectedFile.name}
              </span>
            </div>
            <button
              type="button"
              onClick={clearSelectedFile}
              disabled={sending}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label={t("ccClear")}
            >
              <FiX className="h-4 w-4" />
            </button>
          </div>
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <textarea
            value={draftText}
            onChange={(event) => setDraftText(event.target.value)}
            onKeyDown={handleComposerKeyDown}
            rows={2}
            placeholder={t("ccWhatsAppComposePlaceholder")}
            className="min-h-[44px] flex-1 resize-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/15 disabled:bg-slate-50"
            disabled={sending || !selectedPhone}
            aria-label={t("ccWhatsAppComposePlaceholder")}
          />
          <div className="flex shrink-0 gap-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={ACCEPTED_FILE_TYPES}
              onChange={handleFileSelected}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || !selectedPhone}
              title={t("ccWhatsAppAttachFile")}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
            >
              <FiPaperclip className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={
                sending || !selectedPhone || (!String(draftText || "").trim() && !selectedFile)
              }
              className={`${LISTING_PRIMARY_BUTTON_CLASS} !bg-[#25D366] hover:!bg-[#1ebe57] disabled:opacity-50`}
            >
              <FiSend className="h-4 w-4" />
              {sending ? t("ccProcessing") : t("ccWhatsAppSend")}
            </button>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">{t("ccWhatsAppComposerHint")}</p>
      </div>
    </div>
  );
};

export default TicketWhatsAppThread;
